/**
 * Pure helpers for MergedCreateActionCard's read-mode association disposition:
 * deciding, for each absorbed association, whether it renders as a full card,
 * a compact chip, or not at all — and resolving enrichedData for the primary
 * entity's content component.
 *
 * Extracted from MergedCreateActionCard so that file stays focused on
 * rendering; these functions take no component state and can be tested in
 * isolation.
 */

import type { EnrichedActionData } from '../../lib/proposals/core/types';
import type { MergedAssociation } from '../../lib/proposals/utils/actionMerging';

// Entity types where target_object is used for inline text, not nested cards
// (e.g. RiskCardContent shows the deal name as a subtitle).
const INLINE_TARGET_TYPES = new Set(['ahoy_risk']);

// Per-primary-type set of association types whose target the primary's
// content component already renders INLINE (e.g. ContactCardContent's
// "Company" metadata row). When the association target ALSO owns its own
// top-level create card on the proposal, drop the nested association card
// entirely — the fact already appears in the metadata row. Types NOT listed
// here whose target owns a card render a compact chip instead. Widen only
// alongside a content component verified to render that association inline.
// Mirrors INLINE_TARGET_TYPES / STORE_HYDRATED_PRIMARY_TYPES (in
// MergedCreateActionCard).
export const ASSOCIATIONS_RENDERED_INLINE_BY_PRIMARY_TYPE = new Map<
  string,
  ReadonlySet<string>
>([['ahoy_contact', new Set(['ahoy_contact_to_company'])]]);

/**
 * Why an absorbed association won't be applied, if it won't.
 *
 * - `applies`: it will run.
 * - `unchecked`: its own action is unchecked.
 * - `invalid`: an endpoint it links to won't exist (that endpoint's create is
 *   unchecked). `reason` is the same sentence the disabled checkbox shows —
 *   it names the missing entities, which may be the SOURCE, not the target,
 *   so the chip must not guess at "X is unchecked" on its own.
 */
export type AssociationExclusion =
  | { kind: 'applies' }
  | { kind: 'unchecked' }
  | { kind: 'invalid'; reason: string };

export const APPLIES: AssociationExclusion = { kind: 'applies' };
export const UNCHECKED: AssociationExclusion = { kind: 'unchecked' };

/** One absorbed association with its read-mode disposition. */
export interface VisibleAssociation {
  assoc: MergedAssociation;
  /** Render the target as a compact chip rather than a full card. */
  compact: boolean;
  /** Whether — and why — the association won't be applied. */
  exclusion: AssociationExclusion;
}

/**
 * Strips `target_object` from enrichedData to prevent duplicate rendering —
 * in the merged context, association cards already render each associated
 * entity, so content components that render target_object as a nested card
 * (e.g. DealCardContent's company sub-card) would create duplicates.
 *
 * For inline-target types, the first association's target_object is merged
 * back in so inline-display components (e.g. RiskCardContent's subtitle)
 * still have it.
 */
export function resolveEnrichedDataForDisplay(
  baseEnrichedData: EnrichedActionData | undefined,
  entityTypeName: string,
  mergedAssociations: MergedAssociation[]
): EnrichedActionData | undefined {
  if (!baseEnrichedData) {
    return baseEnrichedData;
  }
  const { target_object: _stripped, ...withoutTarget } = baseEnrichedData;
  void _stripped;

  if (
    INLINE_TARGET_TYPES.has(entityTypeName) &&
    mergedAssociations.length > 0
  ) {
    const firstAssoc = mergedAssociations[0].enrichedData;
    if (firstAssoc?.target_object) {
      return {
        ...withoutTarget,
        target_object: firstAssoc.target_object,
      };
    }
  }
  return withoutTarget;
}

/**
 * Computes the read-mode disposition (full card / compact chip / hidden) for
 * every absorbed association on a merged create action.
 *
 * `exclusionFor` answers whether — and why — the association action won't
 * run (see `AssociationExclusion`). `undefined` (no selection provider) fails
 * open — every association renders as applying.
 */
export function computeVisibleAssociations(
  mergedAssociations: MergedAssociation[],
  topLevelCreatedIds: Set<string>,
  entityType: string,
  exclusionFor: ((actionId: number) => AssociationExclusion) | undefined
): VisibleAssociation[] {
  return mergedAssociations
    .map((assoc): VisibleAssociation | null => {
      // An association that won't be applied must never be silent: always a
      // de-emphasised compact chip, whatever the target. Without this, a
      // contact whose company link was unchecked — or whose company's create
      // was unchecked, which makes the link invalid — would render with no
      // trace of the company at all.
      const exclusion = exclusionFor ? exclusionFor(assoc.action.id) : APPLIES;
      if (exclusion.kind !== 'applies') {
        return { assoc, compact: true, exclusion };
      }
      const targetOwnsCard = topLevelCreatedIds.has(assoc.targetEntityId);
      const renderedInline =
        ASSOCIATIONS_RENDERED_INLINE_BY_PRIMARY_TYPE.get(entityType)?.has(
          assoc.associationType
        ) ?? false;
      // Owns a top-level card AND primary renders it inline → omit entirely.
      if (targetOwnsCard && renderedInline) {
        return null;
      }
      // Owns a card but not inline → compact chip; otherwise full card.
      return { assoc, compact: targetOwnsCard, exclusion };
    })
    .filter((x): x is VisibleAssociation => x !== null);
}
